// Identity presentation only. Do not infer financial information, a probability,
// a preferred entity, or an assessment from provider search candidates.
export const US_STATES = Object.freeze({
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
});

const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const has = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const text = value => typeof value === 'string' ? value.trim() : '';
const normalized = value => text(value).replace(/\./g, '').replace(/\s+/g, ' ').toUpperCase();
const validOptionalText = (value, keys) => keys.every(key => value?.[key] == null || typeof value[key] === 'string');
const stateNames = new Map(Object.entries(US_STATES).map(([code, name]) => [normalized(name), code]));
const US_COUNTRY_NAMES = new Set(['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA']);

export function normalizeUSState(value) {
  const key = normalized(value);
  return has(US_STATES, key) ? key : stateNames.get(key) || null;
}

function stateOf(region) {
  const code = text(region?.abbreviatedName), name = text(region?.name);
  const provided = [code, name].filter(Boolean), known = provided.map(normalizeUSState);
  // Contradictory or unknown metadata cannot turn a candidate into a state match.
  const stateCode = validOptionalText(region, ['abbreviatedName', 'name']) && known.length && known.every(value => value && value === known[0]) ? known[0] : null;
  return { state: name || code, stateCode };
}

function isUS(country) {
  if (!validOptionalText(country, ['isoAlpha2Code', 'name'])) return false;
  const code = text(country?.isoAlpha2Code), name = text(country?.name);
  if (!code && !name) return false;
  return (!code || normalized(code) === 'US') && (!name || US_COUNTRY_NAMES.has(normalized(name)));
}

function confidenceOf(candidate) {
  const quality = candidate.matchQualityInformation;
  if (quality == null) return null;
  if (!record(quality)) throw new Error('D&B returned invalid match-quality information.');
  if (!has(quality, 'confidenceCode') || quality.confidenceCode == null) return null;
  const value = quality.confidenceCode;
  if ((typeof value !== 'string' || !value.trim()) && (typeof value !== 'number' || !Number.isFinite(value))) throw new Error('D&B returned an invalid confidence code.');
  return { label: 'D&B confidence code', value: String(value) };
}

export function parseDnbIdentityResponse(responseData, requestedState) {
  if (!record(responseData) || responseData.success !== true || responseData.error || !record(responseData.data) || responseData.data.error) throw new Error('D&B identity search did not return a valid successful response.');
  const data = responseData.data, groups = ['searchCandidates', 'matchCandidates'].filter(key => has(data, key));
  if (!groups.length || groups.some(key => !Array.isArray(data[key]))) throw new Error('D&B identity search returned an invalid candidate list.');
  const selectedState = normalizeUSState(requestedState);
  const candidates = groups.flatMap(group => data[group].map((candidate, index) => {
    if (!record(candidate) || !record(candidate.organization) || !text(candidate.organization.primaryName)) throw new Error('D&B identity search returned an invalid company candidate.');
    const organization = candidate.organization, primary = organization.primaryAddress;
    if (primary != null && !record(primary)) throw new Error('D&B identity search returned an invalid company address.');
    for (const key of ['streetAddress', 'addressLocality', 'addressRegion', 'addressCountry']) {
      if (primary?.[key] != null && !record(primary[key])) throw new Error('D&B identity search returned invalid address metadata.');
    }
    const duns = organization.duns;
    if (duns != null && !((typeof duns === 'string' || (typeof duns === 'number' && Number.isSafeInteger(duns))) && /^\d{9}$/.test(String(duns)))) throw new Error('D&B identity search returned an invalid D-U-N-S number.');
    const { state, stateCode } = stateOf(primary?.addressRegion);
    const country = text(primary?.addressCountry?.name) || text(primary?.addressCountry?.isoAlpha2Code);
    const address = [text(primary?.streetAddress?.line1), text(primary?.streetAddress?.line2), text(primary?.addressLocality?.name), state, text(primary?.postalCode), country].filter(Boolean).join(', ');
    return {
      id: `${group}-${index}`,
      name: text(organization.primaryName),
      duns: duns == null ? '' : String(duns),
      address, state, stateCode, country,
      confidence: confidenceOf(candidate),
      matchesState: Boolean(selectedState && stateCode === selectedState && isUS(primary?.addressCountry)),
    };
  }));
  return {
    candidates,
    totalCandidates: candidates.length,
    matchedCount: candidates.filter(candidate => candidate.matchesState).length,
    fromCache: typeof responseData.message === 'string' && /\(from cache\)/i.test(responseData.message),
  };
}
